const IV_LENGTH = 16;
const AUTH_TAG_LENGTH = 16;

/**
 * Converts base64 string to Uint8Array
 * @param {string} base64 - Base64 encoded string
 * @returns {Uint8Array} Byte array
 */
const base64ToUint8Array = (base64: any) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

/**
 * Decrypts data encrypted with AES-256-GCM using Web Crypto API
 * @param {string} encryptedData - Encrypted data (base64 encoded) containing IV + encrypted text + auth tag
 * @param {string} key - 32-byte encryption key (base64 encoded)
 * @returns {Promise<string>} Decrypted plain text
 */
export const decrypt = async (encryptedData, key) => {
  try {
    // Convert base64 encrypted data to Uint8Array
    const combined = base64ToUint8Array(encryptedData);

    // Extract IV (first 16 bytes)
    const iv = combined.slice(0, IV_LENGTH);

    // Extract encrypted data + auth tag (everything after IV)
    // Web Crypto API expects the auth tag to be appended to the ciphertext
    const encryptedWithTag = combined.slice(IV_LENGTH);

    // Convert base64 key to Uint8Array
    const keyBuffer = base64ToUint8Array(key);

    // Import the key for Web Crypto API
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );

    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      cryptoKey,
      encryptedWithTag
    );

    // Convert decrypted buffer to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error(`Decryption failed: ${error.message}`);
  }
};
